/* =============================================================================
   NAJIBS — collection filtering and sorting.

   The markup this upgrades is already complete without JavaScript: the facets are a
   real <form method="get"> pointed at the collection URL, and every chip, "clear" and
   pagination control is a real link. This file only removes the page reload — it uses
   the Section Rendering API to swap the facet groups, the chips, the result count and
   the product grid in place, then updates the address bar so the filtered view can be
   shared, bookmarked and navigated back out of.

   No libraries. One custom element: <najibs-facets>.
   ============================================================================= */
(function () {
  'use strict';

  var NAJIBS = (window.NAJIBS = window.NAJIBS || {});

  if (!('customElements' in window) || customElements.get('najibs-facets')) return;

  /* The regions that are re-rendered. The <najibs-drawer> itself is deliberately NOT
     replaced: on mobile it may be open, and swapping it would drop the focus trap. */
  var SWAP_TARGETS = [
    '[data-facets-groups]',
    '[data-facets-chips]',
    '[data-facets-count]',
    '[data-product-grid]'
  ];

  var PHONE = '(max-width: 749px)';
  /* Where the horizontal filter bar actually becomes a row of dropdowns. */
  var DROPDOWNS = '(min-width: 990px)';

  function isPhone() {
    return window.matchMedia(PHONE).matches;
  }

  function attr(value) {
    return String(value == null ? '' : value).replace(/(["\\])/g, '\\$1');
  }

  /* A selector stable enough to survive a re-render, so focus lands back where it was. */
  function selectorFor(element) {
    if (!element || !element.getAttribute) return null;
    if (element.hasAttribute('data-sort-select')) return '[data-sort-select]';
    if (element.id) return '[id="' + attr(element.id) + '"]';

    var name = element.getAttribute('name');
    if (name) {
      var type = (element.getAttribute('type') || '').toLowerCase();
      if (type === 'checkbox' || type === 'radio') {
        return '[name="' + attr(name) + '"][value="' + attr(element.value) + '"]';
      }
      return '[name="' + attr(name) + '"]';
    }

    var href = element.getAttribute('href');
    if (href) return 'a[href="' + attr(href) + '"]';
    return null;
  }

  class NajibsFacets extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.sectionId = this.dataset.sectionId;

      this.onChange = this.onChange.bind(this);
      this.onSubmit = this.onSubmit.bind(this);
      this.onClick = this.onClick.bind(this);
      this.onPopState = this.onPopState.bind(this);
      this.onOutside = this.onOutside.bind(this);

      this.addEventListener('change', this.onChange);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      window.addEventListener('popstate', this.onPopState);
      document.addEventListener('click', this.onOutside);
      document.addEventListener('focusin', this.onOutside);
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      document.removeEventListener('click', this.onOutside);
      document.removeEventListener('focusin', this.onOutside);
      if (this.controller) this.controller.abort();
    }

    get drawer() {
      return this.querySelector('najibs-drawer');
    }

    drawerIsOpen() {
      var drawer = this.drawer;
      return !!(drawer && drawer.classList.contains('is-open'));
    }

    /* ------------------------------------------------------------- listeners */

    onChange(event) {
      var input = event.target.closest('[data-facet-input]');
      if (!input || !input.form) return;

      /* While the drawer is open the visitor confirms with Apply — the grid behind it
         never shifts underneath them mid-choice. */
      if (input.form.matches('[data-facets-form]') && this.drawerIsOpen()) return;

      this.navigate(this.urlFor(input.form));
    }

    onSubmit(event) {
      var form = event.target.closest('form');
      if (!form) return;
      event.preventDefault();

      var drawer = this.drawer;
      var wasOpen = this.drawerIsOpen();

      /* Applying from the drawer closes it, which takes the Apply button — the element
         that had focus — into a display:none subtree. Restoring focus there would drop
         it silently on <body> and lose the visitor's place, so focus is handed to the
         Filter button that reopens the drawer instead, and the fresh results are brought
         to the top of the screen. */
      this.navigate(
        this.urlFor(form),
        wasOpen ? { focus: '.facets-bar__toggle', reveal: true } : null
      );

      if (wasOpen && drawer && typeof drawer.close === 'function') drawer.close();
    }

    /* On a phone the grid that just changed is usually scrolled off the top of the
       screen, so the visitor is left looking at unchanged pixels. */
    revealResults() {
      if (!isPhone()) return;
      var results = this.querySelector('[data-product-grid]');
      if (!results || typeof results.scrollIntoView !== 'function') return;

      var reduced = typeof NAJIBS.prefersReducedMotion === 'function' && NAJIBS.prefersReducedMotion();
      results.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
    }

    onClick(event) {
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      var link = event.target.closest('[data-facet-link]');
      if (!link || !link.href) return;

      event.preventDefault();
      this.navigate(link.href);
    }

    onPopState() {
      this.navigate(window.location.href, { push: false });
    }

    /* Only the desktop horizontal bar turns its facet groups into dropdowns, and only
       there should an outside click close one. Below 990px the very same <details> are
       accordions stacked inside the filter drawer: closing them on any outside touch
       would fight the visitor every time they reached for a second group, the drawer
       header or the Apply row. The media query is the same one the layout uses in
       assets/component-collection.css. */
    onOutside(event) {
      if (this.dataset.layout !== 'horizontal') return;
      if (!window.matchMedia(DROPDOWNS).matches) return;

      this.querySelectorAll('details[data-facet-menu][open]').forEach(function (details) {
        if (!details.contains(event.target)) details.removeAttribute('open');
      });
    }

    /* ------------------------------------------------------------------ core */

    urlFor(form) {
      var action = form.getAttribute('action') || window.location.pathname;
      var params = new URLSearchParams();

      new FormData(form).forEach(function (value, key) {
        if (value === '' || value == null) return;
        params.append(key, value);
      });

      var query = params.toString();
      return query ? action + '?' + query : action;
    }

    captureState() {
      var active = document.activeElement;
      var groups = this.querySelector('[data-facets-groups]');
      var open = [];

      this.querySelectorAll('[data-facet-group]').forEach(function (group) {
        if (group.open && group.id) open.push(group.id);
      });

      return {
        focus: this.contains(active) ? selectorFor(active) : null,
        open: open,
        scroll: groups ? groups.scrollTop : 0
      };
    }

    restoreState(state, focusOverride) {
      this.querySelectorAll('[data-facet-group]').forEach(function (group) {
        if (group.id && state.open.indexOf(group.id) !== -1) group.open = true;
      });

      var groups = this.querySelector('[data-facets-groups]');
      if (groups && state.scroll) groups.scrollTop = state.scroll;

      if (focusOverride) {
        var override = this.querySelector(focusOverride);
        if (override && typeof override.focus === 'function') {
          override.focus({ preventScroll: true });
          return;
        }
      }

      if (!state.focus) return;

      var target = this.querySelector(state.focus);
      if (!target) {
        /* The control that had focus was a chip that has now been removed. */
        target =
          this.querySelector('[data-facets-chips] a') ||
          this.querySelector('.facets-bar__toggle') ||
          this.querySelector('[data-sort-select]');
      }
      if (target && typeof target.focus === 'function') target.focus({ preventScroll: true });
    }

    async navigate(url, options) {
      var opts = options || {};
      var push = opts.push !== false;
      var display = new URL(url, window.location.origin);
      display.searchParams.delete('section_id');

      var request = new URL(display.toString());
      if (this.sectionId) request.searchParams.set('section_id', this.sectionId);

      var state = this.captureState();

      if (this.controller) this.controller.abort();
      this.controller = 'AbortController' in window ? new AbortController() : null;

      this.setAttribute('data-loading', '');

      try {
        var response = await fetch(
          request.toString(),
          this.controller ? { signal: this.controller.signal } : undefined
        );
        if (!response.ok) throw new Error('Facets request failed: ' + response.status);

        var markup = await response.text();
        var parsed = new DOMParser().parseFromString(markup, 'text/html');

        var self = this;
        SWAP_TARGETS.forEach(function (selector) {
          var next = parsed.querySelector(selector);
          var current = self.querySelector(selector);
          if (next && current) current.innerHTML = next.innerHTML;
        });

        if (push) {
          window.history.pushState({ najibsFacets: true }, '', display.toString());
        }

        this.restoreState(state, opts.focus);
        if (opts.reveal) this.revealResults();
        if (typeof NAJIBS.initReveals === 'function') NAJIBS.initReveals(this);
      } catch (error) {
        if (error && error.name === 'AbortError') return;
        console.error(error);
        window.location.href = display.toString();
      } finally {
        this.removeAttribute('data-loading');
      }
    }
  }

  customElements.define('najibs-facets', NajibsFacets);
})();
